/**
 * Google / Apple / GitHub のログイン・連携・再認証の日本語（en.oauth.ts と同じキーを全部持つ。漏れは typecheck で落ちる）。
 * 表記の決め事は ja.ts の冒頭と同じ。文言の案は docs/teams/creator/tasks/dev-oauth-ui.md（Core と合意した案）。
 */
import type { enOAuth } from './en.oauth';
import type { PluralMsg } from './index';

type OAuthMessages = { [K in keyof typeof enOAuth]: (typeof enOAuth)[K] extends string ? string : string | PluralMsg };

export const jaOAuth: OAuthMessages = {
  'oauth.or': 'または',
  'oauth.continueWith': '{provider} で続ける',
  'oauth.callback.working': 'ログインしています…',
  'oauth.callback.invalidTitle': 'ログインが完了しませんでした',

  'oauth.error.access_denied': '{provider} でのログインを取り消しました。',
  'oauth.error.provider_error': '{provider} でログインできませんでした。少し待ってからもう一度お試しください。',
  'oauth.error.email_exists':
    'このメールのアカウントが既にあります。いつもの方法（パスワード等）でログインしてから、アカウント設定の「ログイン方法」で {provider} を追加してください。',
  'oauth.error.verify_email_first':
    'このメールのアカウントの確認が必要です。「パスワードを忘れた」からメールを確認してログインし、設定で {provider} を追加してください。',
  'oauth.error.email_not_verified':
    '{provider} のアカウントに確認済みのメールアドレスが無いため、{product} のアカウントを作れません。{provider} でメールを確認するか、メールとパスワードで登録してください。',
  'oauth.error.disposable_email': '{provider} のアカウントのメールアドレスが使い捨てのアドレスのため、{product} のアカウントを作れません。ふだん使い続けるメールアドレスのアカウントを使ってください。',
  'oauth.error.identity_already_linked': 'この {provider} アカウントは、別の {product} のアカウントで使われています。',
  'oauth.error.provider_already_linked': '別の {provider} アカウントが既に追加されています。追加し直すには、先にそれを外してください。',
  'oauth.error.reauth_mismatch': 'その {provider} アカウントは、この {product} のアカウントに追加したものではありません。追加したのと同じアカウントを選んでください。',
  'oauth.error.invalid_state': 'このログインの手続きは期限が切れたか、別のタブで始めたものです。もう一度はじめからお試しください。',
  'oauth.error.redirectNotAllowed': 'このダッシュボードのアドレスでは、このログイン方法が設定されていません。サーバーの管理者に KUMO_DASHBOARD_URL の確認を頼んでください。',

  'oauth.takeover.title': 'このメールのアカウントでログインしました',
  'oauth.takeover.body': '安全のため、以前のパスワードを使えなくし、ほかの端末のダッシュボードと、つないでいたAIエージェントやターミナルのコマンドをログアウトしました。サーバーだけで使うキー（sk_）も使えなくしたので、「キー（pk_ / sk_）」の画面で作り直すか、AIに「キーを用意して」と頼んでください。',
  'oauth.takeover.hold': 'パスキーも無効にしたので、出金に使うには登録し直してください。安全のため、live の出金の確認は 72 時間止まります。',

  'reauth.title': '本人の確認',
  'reauth.lead': '大事な変更なので、本人であることを確認します。',
  'reauth.failed': '確認できませんでした（パスワードが違うか、確認の期限が切れました）。もう一度お試しください。',
  'reauth.password': '今のパスワード',
  'reauth.confirm': '確認',
  'reauth.confirmWith': '{provider} で確認',
  'reauth.oauthNote': '確認が済むとこの画面に戻ります。確認は 5 分間・1 回だけ有効です。',
  'reauth.noMethod': '今このアカウントで使える確認の方法がありません。ログイン画面の「パスワードを忘れた」からパスワードを設定してください。',

  'account.close.oauthConfirmNote': 'Google・GitHub で登録したアカウントです。ボタンを押した後、そのアカウントで本人の確認をします。',
  'account.close.resumedTitle': '確認できました。アカウントを閉鎖しますか？',
  'account.close.resumedBody': '取り消せません。確認は 5 分間有効です。',

  'login.title': 'ログイン方法',
  'login.lead': 'このアカウントにログインできる方法です。',
  'login.password': 'パスワード',
  'login.passwordSet': '設定済み',
  'login.passwordNotSet': '未設定',
  'login.setPassword': 'パスワードを設定',
  'login.changePassword': 'パスワードを変更',
  'login.notLinked': '未追加',
  'login.add': '{provider} を追加',
  'login.remove': '外す',
  'login.addedOn': '{date} に追加',
  'login.lastUsed': '最後の利用 {date}',
  'login.linked': '{provider} を追加しました。これからは {provider} でもログインできます。',
  'login.unlinked': '{provider} を外しました。',
  'login.lastMethod': '最後のログイン方法は外せません。先にパスワードを設定するか、別の方法を追加してください。',
  'login.noAutoLink': 'メールアドレスが同じでも、Google・GitHub のアカウントを自動では結び付けません。ログインしてから、ここで本人が追加します。',
  'login.removeTitle': '{provider} を外しますか？',
  'login.removeBody': '外すと、{provider} ではログインできなくなります。',
  'login.signOutOthers': 'ほかの端末のダッシュボードと、つないでいるAIエージェントやターミナルのコマンドはログアウトします。このブラウザはログインしたままです。',
  'login.withdrawHold': '安全のため、パスワードの設定・変更の後 72 時間は live の出金の確認が止まります。',
  'login.setPasswordTitle': 'パスワードを設定',
  'login.changePasswordTitle': 'パスワードを変更',
  'login.currentPassword': '今のパスワード',
  'login.currentRequired': '今のパスワードを入れてください。',
  'login.confirmedWith': '{provider} で確認しました。',
  'login.reauthExpired': '確認の期限が切れました。閉じてから、もう一度はじめてください。',
  'login.savePassword': 'パスワードを保存',
  'login.passwordSaved': 'パスワードを保存しました。ほかの端末のダッシュボードと、つないでいたAIエージェントやターミナルのコマンドはログアウトしました。',

  'error.provider_not_configured': 'このサーバーでは、このログイン方法は使えません。',
  'error.reauthentication_required': '続けるには本人の確認が必要です。',
  'error.last_login_method': 'このアカウントの最後のログイン方法です。先にパスワードを設定するか、別のログイン方法を追加してください。'
};
